/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { useRef, useState, useEffect, useCallback } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { Keys, containsKey } from '../utils/UNSAFE_keys';
import { useId } from '../hooks/UNSAFE_useId';
import { ToggleDetail } from '../UNSAFE_Collection';
import type { ViewportConfig } from '../hooks/UNSAFE_useViewportIntersect';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { GroupedListStyles } from 'src/UNSAFE_GroupedList/themes/GroupedListStyles.css';
import { listGroupHeaderMultiVariantStyles } from './themes/ListStyles.css';
import { GroupedListRedwoodTheme } from '../UNSAFE_GroupedList/themes/redwood/GroupedListTheme';

// default style class for items in ListView
export const GROUP_SELECTOR = '[data-oj-listview-group]';
export const STICKY_SELECTOR = '[data-oj-listview-sticky]';

/**
 * Props for the ListViewItem Component
 */
export type Props<K> = {
  children: ComponentChildren;
  itemKey: K;
  itemIndex: number;
  itemDepth: number;
  isFocused: boolean;
  isFocusRingVisible: boolean;
  isActive: boolean;
  isGridlineVisible: boolean;
  isExpandable: boolean;
  expandedKeys: Keys<K>;
  ariaDescribedBy?: string;
  onToggle?: (detail: ToggleDetail<K>) => void;
  viewportConfig?: ViewportConfig;
};

/**
 * The internal component used to render a group header in ExpandableList and GroupedList.
 */
export function ListGroupHeader<K extends string | number>({
  ariaDescribedBy,
  children,
  itemKey,
  itemIndex,
  itemDepth,
  isFocused,
  isFocusRingVisible,
  isActive,
  isGridlineVisible,
  isExpandable,
  expandedKeys,
  viewportConfig
}: Props<K>) {
  const [isStuck, setStuck] = useState(false);

  const { classes: groupedListThemeClasses, styles: groupedListStyles } = useComponentTheme<
    never,
    GroupedListStyles
  >(GroupedListRedwoodTheme);

  const { interactionProps, applyHoverStyle, applyPseudoHoverStyle, applyActiveStyle } =
    useInteractionStyle();

  const variantClasses = listGroupHeaderMultiVariantStyles({
    expandable: isExpandable ? 'isExpandable' : 'notExpandable',
    needsEventsHover: applyHoverStyle ? 'isNeedsEventsHover' : 'notNeedsEventsHover',
    pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
    active: applyActiveStyle || isActive ? 'isActive' : 'notActive',
    focusRingVisible:
      isFocusRingVisible && isFocused ? 'isFocusRingVisible' : 'notFocusRingVisible',
    gridlineTop: itemIndex > 0 ? 'visible' : 'hidden',
    gridlineBottom: isGridlineVisible ? 'visible' : 'hidden'
  });

  const baseClasses = [variantClasses];
  if (isStuck) {
    baseClasses.push(groupedListThemeClasses);
    baseClasses.push(groupedListStyles.stuckHeader);
  }

  const classes = classNames(baseClasses);
  const styleInterpolations = mergeInterpolations<FlexitemProps>([
    ...Object.values(flexitemInterpolations)
  ]);
  const { class: cls, ...cellStyles } = styleInterpolations({
    alignSelf: 'center',
    flex: '1 0 auto'
  });

  // create these refs up front to avoid having to update ref.current values directly during render
  // function or setup a useEffect to update if options/values change in a subsequent render.
  const expandableScrollerRef = useRef<HTMLElement>(null);
  // rely on provided scrollerRef, not to body on first render.
  const scrollerRef = isExpandable ? expandableScrollerRef : viewportConfig?.scrollerRef ?? null;
  const headerRef = useRef<HTMLDivElement>(null);

  // compute stuck state based on scrollTop and element positions; avoids IntersectionObserver edge cases
  const onScroll = useCallback(() => {
    const scroller = scrollerRef ? scrollerRef.current : null;
    const headerEl = headerRef.current;
    if (!scroller || !headerEl) return;

    let isStuck = false;
    const nextEl = headerEl.nextElementSibling;
    if (nextEl) {
      const headerRect = headerEl.getBoundingClientRect();
      // apply sticky styling if next element has started to scroll under this header
      isStuck = headerRect.bottom > nextEl.getBoundingClientRect().top;
      if (!isStuck) {
        let prevEl = headerEl.previousElementSibling;
        // check for this header's top value matching the previous header's top value - the case where
        // this header has replaced the previous header, but the next item has not yet scrolled under it
        while (prevEl) {
          if (prevEl.hasAttribute('data-oj-listview-group')) {
            isStuck = prevEl.getBoundingClientRect().top === headerRect.top;
            break;
          }
          prevEl = prevEl.previousElementSibling;
        }
      }
    }
    setStuck(isStuck);
  }, [scrollerRef]);

  useEffect(() => {
    const scroller = scrollerRef ? scrollerRef.current : null;
    if (!scroller) return;

    onScroll();
    scroller.addEventListener('scroll', onScroll);

    return () => {
      scroller.removeEventListener('scroll', onScroll);
    };
  }, [scrollerRef, onScroll]);

  return (
    <div
      ref={headerRef}
      id={useId()}
      role="row"
      aria-rowindex={itemIndex + 1}
      aria-level={isNaN(itemDepth) ? undefined : itemDepth + 1}
      class={classes}
      data-oj-key={itemKey}
      data-oj-list-item=""
      data-oj-listview-group=""
      {...(isStuck && { 'data-oj-listview-sticky': '' })}
      {...(typeof itemKey === 'number' && { 'data-oj-key-type': 'number' })}
      {...interactionProps}>
      <div
        id={useId()}
        role="gridcell"
        aria-describedby={ariaDescribedBy}
        aria-colindex={1}
        aria-expanded={containsKey(expandedKeys, itemKey)}
        style={cellStyles}>
        <TabbableModeContext.Provider value={{ isTabbable: false }}>
          {children}
        </TabbableModeContext.Provider>
      </div>
    </div>
  );
}
